import React, { useEffect, useMemo, useState } from "react";
import ContentArea from "../ContentArea/ContentArea";
import Layout from "./Layout/Layout";
import StatusMessage from "../StatusMessage/StatusMessage";
import DotChart, { DotChartDatum } from "../DotChart/DotChart";
import AlternativeAdapter from "../../utils/Adapters/AlternativeAdapter";
import { useSeo } from "../../utils/Hooks/useSeo";
import "./FearAndGreedPage.css";

const SEO_DESCRIPTION =
  "Explore Bitcoin Fear and Greed daily readings, rolling averages, and sentiment distribution across market regimes.";

type FearGreedPoint = {
  date: Date;
  value: number;
  classification: string;
};

type RangeKey = "week" | "month" | "year" | "total";

const RANGE_OPTIONS: { key: RangeKey; label: string; days?: number }[] = [
  { key: "week", label: "7D", days: 7 },
  { key: "month", label: "30D", days: 30 },
  { key: "year", label: "1Y", days: 365 },
  { key: "total", label: "All" },
];

const CLASSIFICATION_KEYS = [
  "Extreme Fear",
  "Fear",
  "Neutral",
  "Greed",
  "Extreme Greed",
] as const;

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const formatScore = (value: number) => value.toFixed(0);

const formatAverage = (value: number) => value.toFixed(1);

const toClassificationKey = (classification: string) =>
  classification.toLowerCase().replace(/\s+/g, "-");

const getClassificationByScore = (value: number) => {
  if (value >= 80) return "Extreme Greed";
  if (value >= 60) return "Greed";
  if (value >= 40) return "Neutral";
  if (value >= 20) return "Fear";
  return "Extreme Fear";
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const getAverage = (points: FearGreedPoint[], days: number) => {
  if (!points.length) {
    return 0;
  }

  const now = points[points.length - 1].date.getTime();
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  const filtered = points.filter((point) => point.date.getTime() >= cutoff);
  if (!filtered.length) {
    return 0;
  }

  const total = filtered.reduce((sum, point) => sum + point.value, 0);
  return total / filtered.length;
};

const getCountsByClassification = (points: FearGreedPoint[]) => {
  const counts: Record<string, number> = {};
  CLASSIFICATION_KEYS.forEach((key) => {
    counts[key] = 0;
  });

  points.forEach((point) => {
    counts[point.classification] = (counts[point.classification] ?? 0) + 1;
  });

  return counts;
};

export const FearAndGreedPage = () => {
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState<FearGreedPoint[]>([]);
  const [rangeKey, setRangeKey] = useState<RangeKey>("month");

  useSeo({
    title: "Bitcoin Fear and Greed Index",
    description: SEO_DESCRIPTION,
    canonicalPath: "/fear-greed",
  });

  useEffect(() => {
    const load = async () => {
      const data = (await AlternativeAdapter.getFearAndGreedStats()) as any[];
      const normalized = (Array.isArray(data) ? data : [])
        .map((entry) => ({
          date: new Date(Number(entry.timestamp) * 1000),
          value: clamp(Number(entry.value), 0, 100),
          classification: entry.value_classification as string,
        }))
        .filter((entry) => Number.isFinite(entry.value))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      if (!normalized.length) {
        setPoints([]);
        setLoading(false);
        return;
      }

      setPoints(normalized);
      setLoading(false);
    };

    load();
  }, []);

  const chartData = useMemo<DotChartDatum[]>(
    () =>
      points.map((point) => ({
        date: point.date,
        value: point.value,
        meta: { classification: point.classification },
      })),
    [points],
  );

  const currentPoint = points[points.length - 1];
  const currentClassification = currentPoint
    ? getClassificationByScore(currentPoint.value)
    : "";
  const averages = useMemo(
    () => ({
      week: getAverage(points, 7),
      month: getAverage(points, 30),
      year: getAverage(points, 365),
    }),
    [points],
  );

  const filteredPoints = useMemo(() => {
    if (!points.length) {
      return [];
    }

    const range = RANGE_OPTIONS.find((option) => option.key === rangeKey);
    if (!range?.days) {
      return points;
    }

    const startIndex = Math.max(0, points.length - range.days);
    return points.slice(startIndex);
  }, [points, rangeKey]);

  const classificationCounts = useMemo(
    () => getCountsByClassification(filteredPoints),
    [filteredPoints],
  );

  const legendItems = CLASSIFICATION_KEYS.map((label) => ({
    label,
    key: toClassificationKey(label),
  }));

  const pointClassNameProvider = (datum: DotChartDatum) => {
    const classification = String(datum.meta?.classification ?? "");
    if (!classification) {
      return "";
    }

    return toClassificationKey(classification);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="progress-circle">
          <progress className="pure-material-progress-circular" />
        </div>
      );
    }

    if (!chartData.length) {
      return (
        <StatusMessage
          title="We could not retrieve fear and greed history."
          description="Please try again in a minute."
          minHeightRem={24}
        />
      );
    }

    return (
      <div className="content-lines">
        <section className="fear-greed-overview-card">
          <div className="fear-greed-overview-top">
            <div>
              <h1 className="fear-greed-overview-title">Bitcoin Fear and Greed Index</h1>
              <div className="fear-greed-overview-subtitle">{SEO_DESCRIPTION}</div>
            </div>
            <div className="fear-greed-current-panel">
              <div className="fear-greed-current-label">Current</div>
              <div className="fear-greed-current-value">
                {currentPoint ? formatScore(currentPoint.value) : "--"}
                {currentPoint ? (
                  <span
                    className={`fear-greed-current-dot ${toClassificationKey(
                      currentClassification,
                    )}`}
                    aria-hidden
                  />
                ) : null}
              </div>
              <div className="fear-greed-current-class">
                {currentClassification}
              </div>
            </div>
          </div>

          <div className="fear-greed-stats-grid">
            <div className="fear-greed-stat-card">
              <div className="fear-greed-stat-label">7D Average</div>
              <div className="fear-greed-stat-value">{formatAverage(averages.week)}</div>
            </div>
            <div className="fear-greed-stat-card">
              <div className="fear-greed-stat-label">30D Average</div>
              <div className="fear-greed-stat-value">{formatAverage(averages.month)}</div>
            </div>
            <div className="fear-greed-stat-card">
              <div className="fear-greed-stat-label">1Y Average</div>
              <div className="fear-greed-stat-value">{formatAverage(averages.year)}</div>
            </div>
          </div>
        </section>

        <div className="fear-greed-chart-wrap">
          <DotChart
            data={chartData}
            widthRem={60}
            heightRem={30}
            title="Bitcoin Fear & Greed Index"
            xAxisLabel="Date"
            yAxisLabel="Index value"
            valueFormatter={formatScore}
            yAxisValueFormatter={formatScore}
            dateFormatter={formatDate}
            pointClassNameProvider={pointClassNameProvider}
          />
        </div>

        <section className="fear-greed-distribution">
          <div className="fear-greed-distribution-head">
            <div>
              <h2>Days in each zone</h2>
              <p>Choose a window and see how the mood stacked up.</p>
            </div>
            <div className="fear-greed-range-toggle">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={
                    option.key === rangeKey
                      ? "range-button active"
                      : "range-button"
                  }
                  onClick={() => setRangeKey(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="fear-greed-count-grid">
            {legendItems.map((item) => (
              <div className="fear-greed-count-card" key={item.label}>
                <div className={`fear-greed-count-dot ${item.key}`} />
                <div className="fear-greed-count-label">{item.label}</div>
                <div className="fear-greed-count-value">
                  {classificationCounts[item.label] ?? 0} days
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="powered-by">
          Powered by{" "}
          <a
            className="hashrate-link"
            href="https://alternative.me/crypto/fear-and-greed-index/"
            target="_blank"
            rel="noreferrer"
          >
            alternative.me
          </a>{" "}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <ContentArea isTopArea>
        {renderContent()}
      </ContentArea>
    </Layout>
  );
};
